/**
 * TaskCompletionCheckbox Component
 * Circular checkbox for marking tasks as completed
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  IconButton,
  Tooltip,
  CircularProgress,
  Box
} from '@mui/material';
import {
  Check
} from '@mui/icons-material';
import { COLORS } from '../../shared/colors';
import { updateTicketPartial, updateTaskCompletion } from '../../store/ticketSlice';
import { updateCalendarTicket } from '../../store/calendarSlice';
import { toast } from 'react-toastify';

const TaskCompletionCheckbox = ({ 
  task, 
  size = 'medium',
  onStatusChange,
  disabled = false
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isUpdating, setIsUpdating] = useState(false);
  const [localCompleted, setLocalCompleted] = useState(null);

  // Sync localCompleted with prop changes
  useEffect(() => {
    setLocalCompleted(task.marked_as_completed === true || task.completed === true);
  }, [task.marked_as_completed, task.completed]);

  // Check if task is already completed
  const isCompleted = () => {
    if (localCompleted !== null) return localCompleted;
    return task.marked_as_completed === true || task.completed === true;
  };

  const handleToggleCompletion = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (disabled || isUpdating || !task.id) return;

    setIsUpdating(true);
    
    try {
      const newCompletedState = !isCompleted();

      // Update server first
      await dispatch(updateTicketPartial({
        ticketId: task.id,
        ticketData: { marked_as_completed: newCompletedState }
      })).unwrap();

      // Only update Redux after server confirmation
      dispatch(updateTaskCompletion({ taskId: task.id, completed: newCompletedState }));
      dispatch(updateCalendarTicket({ ticketId: task.id, updates: { completed: newCompletedState } }));

      // Update local state after server success
      setLocalCompleted(newCompletedState);

      // Call callback if provided
      if (onStatusChange) {
        onStatusChange(task.id, newCompletedState);
      }

    } catch (error) {
      console.error('Failed to update task completion status:', error);
      // Revert local state on error
      setLocalCompleted(isCompleted());
      
      const fallbackMsg = t('taskCompletion.error');
      const errorMessage = (typeof error === 'string' ? error : null) || error?.error || error?.message || error?.payload?.error || error?.payload || fallbackMsg;
      toast.error(typeof errorMessage === 'string' ? errorMessage : fallbackMsg);
    } finally {
      setIsUpdating(false);
    }
  };

  const iconSize = size === 'small' ? 16 : size === 'large' ? 24 : 20;
  const buttonSize = size === 'small' ? 'small' : 'medium';

  const completed = isCompleted();
  const tooltipText = completed ? 
    t('taskCompletion.markAsIncomplete', 'Mark as incomplete') : 
    t('taskCompletion.markAsCompleted', 'Mark task as completed');

  return (
    <Tooltip title={tooltipText} arrow placement="top" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
        <IconButton
          size={buttonSize}
          onClick={handleToggleCompletion}
          disabled={disabled || isUpdating}
          sx={{
            p: 0.3,
            // minWidth: 'auto',
            color: completed ? COLORS.success : COLORS.gray[400],
            '&:hover': {
              color: completed ? COLORS.green[700] : COLORS.success,
              bgcolor: completed ? COLORS.green[50] : COLORS.green[50]
            },
            '&:disabled': {
              color: COLORS.gray[300]
            }
          }}
        >
          {isUpdating ? (
            <CircularProgress 
              size={iconSize} 
              thickness={4}
              sx={{ color: COLORS.primary }}
            />
          ) : completed ? (
            <Box sx={{ 
              width: iconSize - 2, 
              height: iconSize - 2, 
              bgcolor: COLORS.success, 
              borderRadius: '50%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <Check sx={{ fontSize: iconSize - 4, color: '#fff', fontWeight: 900 }} />
            </Box>
          ) : (
            <Box sx={{ 
              width: iconSize - 2, 
              height: iconSize - 2, 
              border: `1.5px solid ${COLORS.gray[400]}`, 
              borderRadius: '50%',
              bgcolor: 'transparent'
            }} />
          )}
        </IconButton>
      </Box>
    </Tooltip>
  );
};

export default TaskCompletionCheckbox;